import { PayloadAction } from '@reduxjs/toolkit';
import { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import { CreateClaimSetRequestBody } from '@/domains/claimSets/types/createClaimSet';
import { HttpServiceResponse } from '@/services/HttpService/types';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import {
  getBeneficiaryContactPreferenceUpdateOptions,
  getClaimSetClaimUpdateOptions,
  getClaimSetTaskUpdateOptions,
  getClaimSetUpdateOptions,
  getClaimantUpdateOptions,
  getDocumentDeliveryPreferencesUpdateOptions,
  getGenderUpdateOptions,
  getPolicyUpdateOptions,
} from '@/features/editMode/store/editModeSlice';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import EdsAdapterService from '@/features/eds/services/EdsAdapterService/EdsAdapterService';
import EdsDebugService from '@/features/eds/services/EdsDebugService/EdsDebugService';
import { AllClaimSetsSlots } from '@/domains/claimSets/enums/AllClaimSetsSlots';
import ClaimSetEvents from '@/domains/claimSets/events';
import {
  createClaimSet,
  createClaimSetError,
  createClaimSetSuccess,
  getClaimSetV2,
  getClaimSetV2Success,
  getCurrentClaimSet,
  initClaimSetPage,
  setCurrentClaimSetId,
  startLoadingCreateClaimSet,
} from '@/domains/claimSets/store/claimSetSlice';
import { selectCurrentClaimSetNumber } from '@/domains/claimSets/store/selectors/claimSetSliceSelectors';
import EventService from '@/services/EventService/EventService';
import HttpService from '@/services/HttpService/HttpService';

function* getCurrentClaimSetSaga() {
  const claimSetNumber = yield select(selectCurrentClaimSetNumber);

  if (!claimSetNumber) {
    return;
  }

  try {
    const { data }: HttpServiceResponse<{ listings: { id: number }[] }> = yield call(
      HttpService.post,
      `/filter/${EdsEntityCodes.ClaimSet}`,
      [{ propertyName: 'ClaimSetNumber', values: [String(claimSetNumber)] }],
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    yield put(setCurrentClaimSetId(data.listings[0].id));
  } catch (error) {
    console.error(error);
  }
}

export function* getEDSClaimSetSaga({ payload }: PayloadAction<string>) {
  try {
    const { data } = yield call(
      HttpService.get,
      `/v2/claimSet/${payload}?includeEntities=ReportingUser,Insured.Party.Person,ClaimSetPolicyMatches,Coverages,Milestone,HelperTags`,
      {},
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    const normalizedResponse: NormalizedV2Response = yield call(EdsAdapterService.normalize, data, {
      includeId: true,
    });

    EdsDebugService.logResponse(EdsEntityCodes.ClaimSet, {
      rawResponse: data,
      normalizedResponse,
      dataBySlot: normalizedResponse ? EdsAdapterService.generateSlotRecord<AllClaimSetsSlots>(normalizedResponse) : {},
    });

    yield put(getClaimSetV2Success(normalizedResponse));
  } catch (error) {
    console.error(error);
  }
}

function* createClaimSetSaga(action: PayloadAction<CreateClaimSetRequestBody>) {
  try {
    yield put(startLoadingCreateClaimSet());
    const { data: successMessage }: HttpServiceResponse<string> = yield call(
      HttpService.post,
      `examiner/claim-sets/create-claim-set`,
      action.payload,
    );

    yield put(createClaimSetSuccess());
    EventService.dispatch(new ClaimSetEvents.Created(successMessage));
  } catch (error) {
    yield put(createClaimSetError());
    console.error(error);
  }
}

function* initClaimSetPageSaga() {
  try {
    yield put(getClaimSetUpdateOptions());
    yield put(getClaimSetClaimUpdateOptions());
    yield put(getClaimSetTaskUpdateOptions());
    yield put(getClaimantUpdateOptions());
    yield put(getBeneficiaryContactPreferenceUpdateOptions());
    yield put(getDocumentDeliveryPreferencesUpdateOptions());
    yield put(getGenderUpdateOptions());
    yield put(getPolicyUpdateOptions());
  } catch (error) {
    console.error(error);
  }
}

export function* claimSetWatcher() {
  yield takeLatest(getClaimSetV2.type, getEDSClaimSetSaga);
  yield takeLatest(getCurrentClaimSet.type, getCurrentClaimSetSaga);
  yield takeLatest(createClaimSet.type, createClaimSetSaga);
  yield takeLatest(initClaimSetPage.type, initClaimSetPageSaga);
}
